import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cmdbApi } from '@/api/clients/apiClient'
import { useOrgStore } from '@/stores/orgStore'
import type {
  CmdbInboundProfile,
  CmdbProfileField,
  CreateCmdbProfileRequest,
  UpdateCmdbProfileRequest,
} from '@/api/types'

const QTZ_INBOUND_FIELDS = [
  'external_id', 'inventory_name', 'technology_owner', 'business_owner',
  'security_owner', 'business_unit', 'department', 'criticality',
  'environment', 'support_group', 'lifecycle_stage', 'data_classification', 'tags',
]

// ── View modal ────────────────────────────────────────────────────────────────

interface ViewModalProps {
  profile: CmdbInboundProfile
  onClose: () => void
  onEdit: () => void
}

function ViewModal({ profile, onClose, onEdit }: ViewModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-xl bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-2xl shadow-float flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-light dark:border-border-dark shrink-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-text-light dark:text-text-dark">{profile.name}</h2>
            {profile.is_system && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">system</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {!profile.is_system && (
              <button onClick={onEdit} className="p-1.5 rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-primary/5 hover:text-primary transition-colors" title="Edit">
                <span className="material-icons text-base">edit</span>
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-primary/5 transition-colors">
              <span className="material-icons text-subtle-light dark:text-subtle-dark text-xl">close</span>
            </button>
          </div>
        </div>

        {profile.description && (
          <p className="px-6 pt-4 text-xs text-subtle-light dark:text-subtle-dark">{profile.description}</p>
        )}

        <div className="overflow-y-auto flex-1 px-6 py-4">
          {profile.fields.length === 0 ? (
            <p className="text-xs text-subtle-light dark:text-subtle-dark text-center py-8">No field mappings defined.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border-light dark:border-border-dark">
                  <th className="pb-2 text-left font-medium text-subtle-light dark:text-subtle-dark">QTZ Field</th>
                  <th className="pb-2 w-6"></th>
                  <th className="pb-2 text-left font-medium text-subtle-light dark:text-subtle-dark">CMDB Field</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-light dark:divide-border-dark">
                {profile.fields.map((f, i) => (
                  <tr key={i}>
                    <td className="py-2 font-mono text-text-light dark:text-text-dark">{f.qtz_field}</td>
                    <td className="py-2 text-center text-subtle-light dark:text-subtle-dark">←</td>
                    <td className="py-2 font-mono text-text-light dark:text-text-dark">{f.cmdb_field}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-3 border-t border-border-light dark:border-border-dark shrink-0">
          <p className="text-[11px] text-subtle-light dark:text-subtle-dark">{profile.fields.length} field mapping{profile.fields.length !== 1 ? 's' : ''}</p>
        </div>
      </div>
    </div>
  )
}

// ── Profile modal ─────────────────────────────────────────────────────────────

interface ProfileModalProps {
  profile?: CmdbInboundProfile
  profiles?: CmdbInboundProfile[]
  onClose: () => void
}

function ProfileModal({ profile, profiles = [], onClose }: ProfileModalProps) {
  const { currentOrgId: orgId } = useOrgStore()
  const queryClient = useQueryClient()
  const isEdit = !!profile

  const [name, setName] = useState(profile?.name ?? '')
  const [fields, setFields] = useState<CmdbProfileField[]>(
    profile?.fields ?? [{ qtz_field: 'external_id', cmdb_field: '' }]
  )
  const [error, setError] = useState('')

  const applyTemplate = (templateId: string) => {
    const tmpl = profiles.find(p => p.id === templateId)
    if (!tmpl) return
    setFields(tmpl.fields.map(f => ({ ...f })))
    if (!name) setName(`${tmpl.name} (copy)`)
  }

  const mutation = useMutation({
    mutationFn: (req: CreateCmdbProfileRequest | UpdateCmdbProfileRequest) =>
      isEdit
        ? cmdbApi.updateProfile(orgId, profile!.id, req as UpdateCmdbProfileRequest)
        : cmdbApi.createProfile(orgId, req as CreateCmdbProfileRequest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cmdb-inbound-profiles', orgId] })
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  const updateField = (idx: number, key: keyof CmdbProfileField, val: string) =>
    setFields((f) => f.map((r, i) => (i === idx ? { ...r, [key]: val } : r)))

  const addField = () => setFields((f) => [...f, { qtz_field: QTZ_INBOUND_FIELDS[0], cmdb_field: '' }])

  const removeField = (idx: number) => setFields((f) => f.filter((_, i) => i !== idx))

  const handleSubmit = () => {
    if (!name.trim()) return setError('Name required')
    setError('')
    mutation.mutate(isEdit ? { name, fields } : { name, direction: 'inbound' as const, fields })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-2xl shadow-float flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-light dark:border-border-dark shrink-0">
          <h2 className="text-base font-semibold text-text-light dark:text-text-dark">
            {isEdit ? 'Edit Inbound Profile' : 'New Inbound Profile'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-primary/5 transition-colors">
            <span className="material-icons text-subtle-light dark:text-subtle-dark text-xl">close</span>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {!isEdit && profiles.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">Start from template (optional)</label>
              <select
                defaultValue=""
                onChange={(e) => applyTemplate(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="">— blank profile —</option>
                {profiles.map(p => (
                  <option key={p.id} value={p.id}>{p.name}{p.is_system ? ' (system)' : ''}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">Profile Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. My ServiceNow Mapping"
              className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark">Field Mappings</p>
            <div className="space-y-2">
              {fields.map((f, i) => (
                <div key={i} className="flex items-center gap-2">
                  <select
                    value={f.qtz_field}
                    onChange={(e) => updateField(i, 'qtz_field', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark focus:outline-none focus:ring-1 focus:ring-primary/40"
                  >
                    {QTZ_INBOUND_FIELDS.map((mf) => <option key={mf} value={mf}>{mf}</option>)}
                  </select>
                  <span className="material-icons text-subtle-light dark:text-subtle-dark text-sm">arrow_back</span>
                  <input
                    value={f.cmdb_field}
                    onChange={(e) => updateField(i, 'cmdb_field', e.target.value)}
                    placeholder="cmdb_field_name"
                    className="flex-1 px-2 py-1.5 text-xs font-mono rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-1 focus:ring-primary/40"
                  />
                  <button onClick={() => removeField(i)} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-subtle-light dark:text-subtle-dark hover:text-error transition-colors">
                    <span className="material-icons text-sm">close</span>
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={addField}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg text-primary hover:bg-primary/5 transition-colors"
            >
              <span className="material-icons text-sm">add</span>
              Add field
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <span className="material-icons text-error text-base">error_outline</span>
              <p className="text-sm text-error">{error}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border-light dark:border-border-dark shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-primary/5 transition-colors">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Profile'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── CmdbMappingProfiles (page) ────────────────────────────────────────────────

export default function CmdbMappingProfiles({
  asTab = false,
  showCreate: externalShowCreate,
  onShowCreateChange,
}: {
  asTab?: boolean
  showCreate?: boolean
  onShowCreateChange?: (v: boolean) => void
} = {}) {
  const { currentOrgId: orgId } = useOrgStore()
  const queryClient = useQueryClient()
  const [localShowCreate, setLocalShowCreate] = useState(false)
  const isCreating = asTab && externalShowCreate !== undefined ? externalShowCreate : localShowCreate
  const setIsCreating = asTab && onShowCreateChange ? onShowCreateChange : setLocalShowCreate
  const [viewing, setViewing] = useState<CmdbInboundProfile | undefined>()
  const [editing, setEditing] = useState<CmdbInboundProfile | undefined>()
  const [confirmDelete, setConfirmDelete] = useState<CmdbInboundProfile | undefined>()

  const { data: resp, isLoading } = useQuery({
    queryKey: ['cmdb-inbound-profiles', orgId],
    queryFn: () => cmdbApi.listProfiles(orgId, 'inbound'),
    enabled: !!orgId,
  })

  const deleteMutation = useMutation({
    mutationFn: (pid: string) => cmdbApi.deleteProfile(orgId, pid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cmdb-inbound-profiles', orgId] })
      setConfirmDelete(undefined)
    },
  })

  const profiles = resp ?? []

  return (
    <div className={asTab ? 'space-y-6' : 'p-6 max-w-5xl mx-auto space-y-6'}>
      {!asTab && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/cmdb" className="flex items-center gap-1 text-xs text-subtle-light dark:text-subtle-dark hover:text-primary transition-colors">
              <span className="material-icons text-sm">arrow_back</span>
              CMDB
            </Link>
            <span className="text-subtle-light dark:text-subtle-dark">/</span>
            <h1 className="text-xl font-semibold text-text-light dark:text-text-dark">Inbound Mapping Profiles</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/cmdb/outbound-profiles" className="px-3 py-2 text-sm font-medium rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-primary/5 transition-colors">
              Outbound Profiles
            </Link>
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
            >
              <span className="material-icons text-base">add</span>
              New Profile
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16"><span className="material-icons animate-spin text-subtle-light dark:text-subtle-dark text-2xl">sync</span></div>
      ) : (
        <div className="space-y-3">
          {profiles.map((p) => (
            <div key={p.id} className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-2xl shadow-ambient p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-text-light dark:text-text-dark">{p.name}</p>
                  {p.is_system && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">system</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setViewing(p)}
                    className="p-1.5 rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-primary/5 hover:text-primary transition-colors"
                    title="View mappings"
                  >
                    <span className="material-icons text-base">visibility</span>
                  </button>
                  {!p.is_system && (
                    <>
                      <button onClick={() => setEditing(p)} className="p-1.5 rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-primary/5 hover:text-text-light dark:hover:text-text-dark transition-colors">
                        <span className="material-icons text-base">edit</span>
                      </button>
                      <button onClick={() => setConfirmDelete(p)} className="p-1.5 rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-error transition-colors">
                        <span className="material-icons text-base">delete_outline</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {p.fields.slice(0, 6).map((f, i) => (
                  <span key={i} className="text-[11px] font-mono px-2 py-0.5 rounded bg-container-light dark:bg-container-dark text-text-light dark:text-text-dark">
                    {f.qtz_field} ← {f.cmdb_field}
                  </span>
                ))}
                {p.fields.length > 6 && (
                  <button
                    onClick={() => setViewing(p)}
                    className="text-[11px] px-2 py-0.5 rounded bg-container-light dark:bg-container-dark text-primary hover:bg-primary/10 transition-colors"
                  >
                    +{p.fields.length - 6} more
                  </button>
                )}
              </div>
            </div>
          ))}

          {profiles.length === 0 && (
            <div className="text-center py-16">
              <p className="text-sm text-subtle-light dark:text-subtle-dark">No inbound profiles yet.</p>
            </div>
          )}
        </div>
      )}

      {isCreating && <ProfileModal profiles={profiles} onClose={() => setIsCreating(false)} />}
      {editing && <ProfileModal profile={editing} onClose={() => setEditing(undefined)} />}
      {viewing && (
        <ViewModal
          profile={viewing}
          onClose={() => setViewing(undefined)}
          onEdit={() => { setEditing(viewing); setViewing(undefined) }}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-2xl shadow-float p-6 space-y-4">
            <p className="text-sm font-semibold text-text-light dark:text-text-dark">Delete "{confirmDelete.name}"?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(undefined)} className="px-4 py-2 text-sm font-medium rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-primary/5 transition-colors">Cancel</button>
              <button
                onClick={() => deleteMutation.mutate(confirmDelete.id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-error text-white hover:bg-error/90 disabled:opacity-50 transition-colors"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
